import { LOGOUT_REQUEST } from '@sit/client-shared';
import { produce } from 'immer';
import moment from 'moment';
import { handleActions } from 'redux-actions';
import { mergeDoneApprovalsPage } from '../../helpers/approvals';
import * as types from './types';

const initialState = {
  done: {
    all: {
      error: null,
      initialLoad: false,
      loading: false,
      results: {},
      saving: false,
      totalItems: 0,
      groupBy: 'employee',
      search: '',
    },
    timesheet: {
      groups: {},
      error: null,
      initialLoad: false,
      loading: false,
      saving: false,
      timesheet: {},
      timesheetId: null,
      totalItems: 0,
    },
  },
  todo: {
    actionLoading: false,
    active: {
      groups: {},
      error: null,
      initialLoad: false,
      loading: false,
      saving: false,
      groupBy: null,
      groupId: null,
      totalItems: 0,
    },
    selected: {
      groups: {},
      error: null,
      initialLoad: false,
      loading: false,
      saving: false,
      groupBy: null,
      groupIds: [],
      totalItems: 0,
    },
    sidebar: {
      error: null,
      initialLoad: false,
      loading: false,
      groups: {},
      saving: false,
      totalItems: 0,
      groupBy: 'employee',
      search: '',
    },
  },
};

const approveDeclineByTimesheetsOrEntriesRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.actionLoading = true;
  });
};
const approveDeclineByTimesheetsOrEntriesRequestFailure = (state) => {
  return produce(state, (draft) => {
    draft.todo.actionLoading = false;
  });
};
const approveDeclineByTimesheetsOrEntriesRequestSuccess = (state, payload) => {
  return produce(state, (draft) => {
    draft.todo.actionLoading = false;
  });
};

/**
 *      DONE
 */

const getDoneApprovalsAllRequest = (state) => {
  return produce(state, (draft) => {
    draft.done.timesheet.groups = {};
    draft.done.timesheet.error = null;
    draft.done.timesheet.loading = false;
    draft.done.timesheet.initialLoad = false;
    draft.done.timesheet.timesheetId = {};
    draft.done.all.error = null;
    draft.done.all.loading = true;
    draft.done.all.results = {};
    draft.done.all.totalItems = 0;
    draft.done.all.pageNumber = 0;
    draft.done.all.search = '';
  });
};
const getDoneApprovalsAllRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.done.all.error = action.error;
    draft.done.all.loading = false;
  });
};
const getDoneApprovalsAllRequestSuccess = (state, payload) => {
  const { results, count, pageNumber, pageSize, groupBy, search } = payload.data;
  const resultsToObject = (results || []).reduce((obj, result) => {
    obj[result.title] = result;
    return obj;
  }, {});
  return produce(state, (draft) => {
    draft.done.all.error = null;
    draft.done.all.initialLoad = true;
    draft.done.all.loading = false;
    draft.done.all.results = resultsToObject;
    draft.done.all.totalItems = count;
    draft.done.all.pageNumber = pageNumber;
    draft.done.all.pageSize = pageSize;
    draft.done.all.groupBy = groupBy;
    draft.done.all.search = search;
  });
};
const getDoneApprovalsAllNextPageRequest = (state) => {
  return produce(state, (draft) => {
    draft.done.all.loading = true;
  });
};
const getDoneApprovalsAllNextPageRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.done.all.error = action.error;
    draft.done.all.loading = false;
  });
};
const getDoneApprovalsAllNextPageRequestSuccess = (state, payload) => {
  const { results } = payload.data;
  const resultsToObject = (results || []).reduce((obj, result) => {
    const existingResult = state?.done?.all?.results?.[result.title];
    if (existingResult?.title) {
      obj[result.title] = {
        ...existingResult,
        totalHours: result.totalHours + existingResult.totalHours,
        totalBillableHours: result.totalBillableHours + existingResult.totalBillableHours,
        totalNonBillableHours: result.totalNonBillableHours + existingResult.totalNonBillableHours,
        rows: [...existingResult.rows, ...result.rows],
      };
    } else {
      obj[result.title] = result;
    }
    return obj;
  }, {});
  return produce(state, (draft) => {
    draft.done.all.error = null;
    draft.done.all.loading = false;
    draft.done.all.results = Object.assign(draft.done.all.results, resultsToObject);
    draft.done.all.pageNumber = draft.done.all.pageNumber + 1;
  });
};

const getDoneApprovalsTimesheetRequest = (state) => {
  return produce(state, (draft) => {
    draft.done.timesheet.groups = {};
    draft.done.timesheet.error = null;
    draft.done.timesheet.loading = true;
    draft.done.timesheet.initialLoad = false;
    draft.done.timesheet.timesheetId = null;
    draft.done.timesheet.timesheet = {};
  });
};
const getDoneApprovalsTimesheetRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.done.timesheet.error = action.error;
    draft.done.timesheet.loading = false;
    draft.done.timesheet.initialLoad = false;
  });
};
const getDoneApprovalsTimesheetRequestSuccess = (state, payload) => {
  const { data, timesheetId, orderBy, pageNumber } = payload.data;
  const { count, groups, timesheet } = data;

  const mapTitle = (title) => {
    return orderBy === 'date' ? moment(title, 'YYYY-MM-DD').format('dddd, MMM DD') : title;
  };

  return produce(state, (draft) => {
    draft.done.timesheet.groups = mergeDoneApprovalsPage(groups, draft.done.timesheet.groups, mapTitle);
    draft.done.timesheet.totalItems = count;
    draft.done.timesheet.error = null;
    draft.done.timesheet.loading = false;
    draft.done.timesheet.initialLoad = true;
    draft.done.timesheet.orderBy = orderBy;
    draft.done.timesheet.timesheetId = timesheetId;
    draft.done.timesheet.timesheet = timesheet;
    draft.done.timesheet.pageNumber = pageNumber;
  });
};

const getDoneApprovalsTimesheetNextPageRequest = (state) => {
  return produce(state, (draft) => {
    draft.done.timesheet.loading = true;
  });
};
const getDoneApprovalsTimesheetNextPageRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.done.timesheet.error = action.error;
    draft.done.timesheet.loading = false;
  });
};
const getDoneApprovalsTimesheetNextPageRequestSuccess = (state, payload) => {
  const { data, orderBy } = payload.data;

  const mapTitle = (title) => {
    return orderBy === 'date' ? moment(title, 'YYYY-MM-DD').format('dddd, MMM DD') : title;
  };

  return produce(state, (draft) => {
    draft.done.timesheet.error = null;
    draft.done.timesheet.loading = false;
    draft.done.timesheet.groups = mergeDoneApprovalsPage(data.groups, draft.done.timesheet.groups, mapTitle);
    draft.done.timesheet.pageNumber = draft.done.timesheet.pageNumber + 1;
  });
};
/**
 *      TODO
 */

const getTodoApprovalsGroupsRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.selected.groups = {};
    draft.todo.selected.error = null;
    draft.todo.selected.loading = false;
    draft.todo.selected.initialLoad = false;
    draft.todo.selected.groupBy = null;
    draft.todo.selected.groupIds = [];
    draft.todo.sidebar.error = null;
    draft.todo.sidebar.loading = true;
    draft.todo.sidebar.initialLoad = false;
    draft.todo.sidebar.groups = {};
    draft.todo.sidebar.totalItems = 0;
    draft.todo.sidebar.pageNumber = 0;
    draft.todo.sidebar.search = '';
  });
};
const getTodoApprovalsGroupsRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.todo.sidebar.error = action.error;
    draft.todo.sidebar.loading = false;
  });
};
const getTodoApprovalsGroupsRequestSuccess = (state, payload) => {
  const { groups, count, pageNumber, pageSize, groupBy, search } = payload.data;
  const groupsToObject = (groups || []).reduce((obj, group) => {
    obj[group.title] = group;
    return obj;
  }, {});
  return produce(state, (draft) => {
    draft.todo.sidebar.error = null;
    draft.todo.sidebar.initialLoad = true;
    draft.todo.sidebar.loading = false;
    draft.todo.sidebar.groups = groupsToObject;
    draft.todo.sidebar.totalItems = count;
    draft.todo.sidebar.pageNumber = pageNumber;
    draft.todo.sidebar.pageSize = pageSize;
    draft.todo.sidebar.groupBy = groupBy;
    draft.todo.sidebar.search = search;
  });
};
const getTodoApprovalsGroupsNextPageRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.sidebar.loading = true;
  });
};
const getTodoApprovalsGroupsNextPageRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.todo.sidebar.error = action.error;
    draft.todo.sidebar.loading = false;
  });
};
const getTodoApprovalsGroupsNextPageRequestSuccess = (state, payload) => {
  const { groups } = payload.data;
  const groupsToObject = (groups || []).reduce((obj, group) => {
    const existingGroup = state?.todo?.sidebar?.groups[group.title];
    if (existingGroup?.title) {
      obj[group.title] = {
        ...existingGroup,
        results: [...existingGroup.results, ...group.results],
      };
    } else {
      obj[group.title] = group;
    }
    return obj;
  }, {});
  return produce(state, (draft) => {
    draft.todo.sidebar.error = null;
    draft.todo.sidebar.loading = false;
    draft.todo.sidebar.groups = Object.assign(draft.todo.sidebar.groups, groupsToObject);
    draft.todo.sidebar.pageNumber = draft.todo.sidebar.pageNumber + 1;
  });
};

const getTodoApprovalsActiveRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.active.groups = {};
    draft.todo.active.error = null;
    draft.todo.active.loading = true;
    draft.todo.active.initialLoad = false;
    draft.todo.active.pageNumber = 0;
    draft.todo.active.hasMore = true;
  });
};
const getTodoApprovalsActiveRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.todo.active.error = action.error;
    draft.todo.active.loading = false;
    draft.todo.active.initialLoad = false;
  });
};
const getTodoApprovalsActiveRequestSuccess = (state, payload) => {
  const { data, groupBy, groupId, orderBy, pageNumber, pageSize } = payload.data;
  const { count, groups } = data;

  const groupsObject = groups.reduce((obj, group) => {
    if (orderBy === 'date') {
      const formattedDate = moment(group.title, 'YYYY-MM-DD').format('dddd, MMM DD');
      obj[formattedDate] = {
        ...group,
        title: formattedDate,
      };
    } else {
      obj[group.title] = group;
    }
    return obj;
  }, {});

  return produce(state, (draft) => {
    draft.todo.active.groups = groupsObject;
    draft.todo.active.totalItems = count;
    draft.todo.active.error = null;
    draft.todo.active.loading = false;
    draft.todo.active.initialLoad = true;
    draft.todo.active.orderBy = orderBy;
    draft.todo.active.groupBy = groupBy;
    draft.todo.active.groupId = groupId;
    draft.todo.active.pageNumber = pageNumber;
    draft.todo.active.pageSize = pageSize;
    draft.todo.active.hasMore = groups.length > 0;
  });
};

const getTodoApprovalsActiveNextPageRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.active.loading = true;
  });
};
const getTodoApprovalsActiveNextPageRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.todo.active.error = action.error;
    draft.todo.active.loading = false;
  });
};
const getTodoApprovalsActiveNextPageRequestSuccess = (state, payload) => {
  const { groups, orderBy } = payload.data;
  const groupsObject = groups.reduce((obj, group) => {
    const title = orderBy === 'date' ? moment(group.title, 'YYYY-MM-DD').format('dddd, MMM DD') : group.title;
    const existingGroup = state.todo?.active?.groups[title];
    if (existingGroup) {
      const additionalResults = group.results.filter((result) => !existingGroup.results.find((r) => r.id === result.id));
      const additionalHours = additionalResults.reduce((totalHours, current) => totalHours + current.totalHours, 0);
      obj[title] = {
        ...existingGroup,
        title,
        totalHours: existingGroup.totalHours + additionalHours,
        results: [...existingGroup.results, ...additionalResults],
      };
    } else {
      obj[title] = { ...group, title };
    }
    return obj;
  }, {});

  return produce(state, (draft) => {
    draft.todo.active.error = null;
    draft.todo.active.loading = false;
    draft.todo.active.groups = Object.assign(draft.todo.active.groups, groupsObject);
    draft.todo.active.pageNumber = draft.todo.active.pageNumber + 1;
    draft.todo.active.hasMore = groups.length > 0;
  });
};

const getTodoApprovalsSelectedRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.selected.groups = {};
    draft.todo.selected.error = null;
    draft.todo.selected.loading = true;
    draft.todo.selected.initialLoad = false;
    draft.todo.selected.groupBy = null;
    draft.todo.selected.groupIds = [];
    draft.todo.selected.totalItems = 0;
    draft.todo.selected.pageNumber = 0;
  });
};
const getTodoApprovalsSelectedRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.todo.selected.error = action.error;
    draft.todo.selected.loading = false;
    draft.todo.selected.initialLoad = false;
  });
};
const getTodoApprovalsSelectedRequestSuccess = (state, payload) => {
  const { data, groupBy, groupIds, pageNumber, pageSize } = payload.data;
  const { count, groups } = data;

  const groupsObject = groups.reduce((obj, group) => {
    obj[group.title] = group;
    return obj;
  }, {});

  return produce(state, (draft) => {
    draft.todo.selected.groups = groupsObject;
    draft.todo.selected.totalItems = count;
    draft.todo.selected.error = null;
    draft.todo.selected.loading = false;
    draft.todo.selected.initialLoad = true;
    draft.todo.selected.groupBy = groupBy;
    draft.todo.selected.groupIds = groupIds;
    draft.todo.selected.pageNumber = pageNumber;
    draft.todo.selected.pageSize = pageSize;
  });
};

const getTodoApprovalsSelectedNextPageRequest = (state) => {
  return produce(state, (draft) => {
    draft.todo.selected.loading = true;
  });
};
const getTodoApprovalsSelectedNextPageRequestFailure = (state, action) => {
  return produce(state, (draft) => {
    draft.todo.selected.error = action.error;
    draft.todo.selected.loading = false;
  });
};
const getTodoApprovalsSelectedNextPageRequestSuccess = (state, payload) => {
  const { groups } = payload.data;
  const groupsObject = groups.reduce((obj, group) => {
    const title = group.title;
    const existingGroup = state.todo?.selected?.groups[title];
    if (existingGroup) {
      obj[title] = {
        ...existingGroup,
        title,
        totalHours: existingGroup.totalHours + group.totalHours,
        rows: [...existingGroup.rows, ...group.rows],
      };
    } else {
      obj[title] = group;
    }
    return obj;
  }, {});

  return produce(state, (draft) => {
    draft.todo.selected.error = null;
    draft.todo.selected.loading = false;
    draft.todo.selected.groups = Object.assign(draft.todo.selected.groups, groupsObject);
    draft.todo.selected.pageNumber = draft.todo.selected.pageNumber + 1;
  });
};

const logoutRequest = () => initialState;

export default handleActions(
  {
    [types.APPROVE_DECLINE_BY_TIMESHEETS_OR_ENTRIES_ASYNC_REQUEST]: approveDeclineByTimesheetsOrEntriesRequest,
    [types.APPROVE_DECLINE_BY_TIMESHEETS_OR_ENTRIES_REQUEST_FAILURE]: approveDeclineByTimesheetsOrEntriesRequestFailure,
    [types.APPROVE_DECLINE_BY_TIMESHEETS_OR_ENTRIES_REQUEST_SUCCESS]: approveDeclineByTimesheetsOrEntriesRequestSuccess,
    [types.GET_DONE_APPROVALS_ALL_REQUEST]: getDoneApprovalsAllRequest,
    [types.GET_DONE_APPROVALS_ALL_REQUEST_FAILURE]: getDoneApprovalsAllRequestFailure,
    [types.GET_DONE_APPROVALS_ALL_REQUEST_SUCCESS]: getDoneApprovalsAllRequestSuccess,
    [types.GET_DONE_APPROVALS_ALL_NEXT_PAGE_REQUEST]: getDoneApprovalsAllNextPageRequest,
    [types.GET_DONE_APPROVALS_ALL_NEXT_PAGE_REQUEST_FAILURE]: getDoneApprovalsAllNextPageRequestFailure,
    [types.GET_DONE_APPROVALS_ALL_NEXT_PAGE_REQUEST_SUCCESS]: getDoneApprovalsAllNextPageRequestSuccess,
    [types.GET_DONE_APPROVALS_TIMESHEET_REQUEST]: getDoneApprovalsTimesheetRequest,
    [types.GET_DONE_APPROVALS_TIMESHEET_REQUEST_FAILURE]: getDoneApprovalsTimesheetRequestFailure,
    [types.GET_DONE_APPROVALS_TIMESHEET_REQUEST_SUCCESS]: getDoneApprovalsTimesheetRequestSuccess,
    [types.GET_DONE_APPROVALS_TIMESHEET_NEXT_PAGE_REQUEST]: getDoneApprovalsTimesheetNextPageRequest,
    [types.GET_DONE_APPROVALS_TIMESHEET_NEXT_PAGE_REQUEST_FAILURE]: getDoneApprovalsTimesheetNextPageRequestFailure,
    [types.GET_DONE_APPROVALS_TIMESHEET_NEXT_PAGE_REQUEST_SUCCESS]: getDoneApprovalsTimesheetNextPageRequestSuccess,
    [types.GET_TODO_APPROVALS_GROUPS_REQUEST]: getTodoApprovalsGroupsRequest,
    [types.GET_TODO_APPROVALS_GROUPS_REQUEST_FAILURE]: getTodoApprovalsGroupsRequestFailure,
    [types.GET_TODO_APPROVALS_GROUPS_REQUEST_SUCCESS]: getTodoApprovalsGroupsRequestSuccess,
    [types.GET_TODO_APPROVALS_GROUPS_NEXT_PAGE_REQUEST]: getTodoApprovalsGroupsNextPageRequest,
    [types.GET_TODO_APPROVALS_GROUPS_NEXT_PAGE_REQUEST_FAILURE]: getTodoApprovalsGroupsNextPageRequestFailure,
    [types.GET_TODO_APPROVALS_GROUPS_NEXT_PAGE_REQUEST_SUCCESS]: getTodoApprovalsGroupsNextPageRequestSuccess,
    [types.GET_TODO_APPROVALS_ACTIVE_REQUEST]: getTodoApprovalsActiveRequest,
    [types.GET_TODO_APPROVALS_ACTIVE_REQUEST_FAILURE]: getTodoApprovalsActiveRequestFailure,
    [types.GET_TODO_APPROVALS_ACTIVE_REQUEST_SUCCESS]: getTodoApprovalsActiveRequestSuccess,
    [types.GET_TODO_APPROVALS_ACTIVE_NEXT_PAGE_REQUEST]: getTodoApprovalsActiveNextPageRequest,
    [types.GET_TODO_APPROVALS_ACTIVE_NEXT_PAGE_REQUEST_FAILURE]: getTodoApprovalsActiveNextPageRequestFailure,
    [types.GET_TODO_APPROVALS_ACTIVE_NEXT_PAGE_REQUEST_SUCCESS]: getTodoApprovalsActiveNextPageRequestSuccess,
    [types.GET_TODO_APPROVALS_SELECTED_REQUEST]: getTodoApprovalsSelectedRequest,
    [types.GET_TODO_APPROVALS_SELECTED_REQUEST_FAILURE]: getTodoApprovalsSelectedRequestFailure,
    [types.GET_TODO_APPROVALS_SELECTED_REQUEST_SUCCESS]: getTodoApprovalsSelectedRequestSuccess,
    [types.GET_TODO_APPROVALS_SELECTED_NEXT_PAGE_REQUEST]: getTodoApprovalsSelectedNextPageRequest,
    [types.GET_TODO_APPROVALS_SELECTED_NEXT_PAGE_REQUEST_FAILURE]: getTodoApprovalsSelectedNextPageRequestFailure,
    [types.GET_TODO_APPROVALS_SELECTED_NEXT_PAGE_REQUEST_SUCCESS]: getTodoApprovalsSelectedNextPageRequestSuccess,
    [LOGOUT_REQUEST]: logoutRequest,
  },
  initialState,
);
